// Billing Service API Client

import { BaseApiClient } from '../../../apiClients/BaseApiClient';
import {
  ApproveDepositParams,
  CancelDepositParams,
  CreateChargeRequest,
  CreateDepositRequest,
  GetPaymentsParams,
  GetPaymentsResponse,
  GetUserAccountsParams,
  GetUserAccountsResponse,
  ParsedExternalId,
} from './types';

class BillingApiClient extends BaseApiClient {
  constructor() {
    super({ prefix: 'payment' });
  }

  // === PAYMENTS API ===

  /**
   * Get payments/transaction events with pagination
   */
  async getPayments(params: GetPaymentsParams): Promise<GetPaymentsResponse> {
    return this.makeRequest<GetPaymentsResponse>('/payments/', {
      params: {
        limit: params.limit,
        typeId: params.typeId,
        userId: params.userId,
        'cursor.createdAt': params.cursor?.createdAt,
        'cursor.id': params.cursor?.id,
      },
    });
  }

  /**
   * Create a new deposit transaction
   */
  async createDeposit(request: CreateDepositRequest): Promise<void> {
    await this.makeRequest<object>('/payments/deposit', {
      method: 'POST',
      data: request,
    });
  }

  /**
   * Approve a pending deposit (admin only)
   */
  async approveDeposit(params: ApproveDepositParams): Promise<void> {
    await this.makeRequest<object>(
      `/payments/deposits/${params.eventId}/approve`,
      {
        method: 'POST',
      }
    );
  }

  /**
   * Cancel a pending deposit (admin or user who created it)
   */
  async cancelDeposit(params: CancelDepositParams): Promise<void> {
    await this.makeRequest<object>(
      `/payments/deposits/${params.eventId}/cancel`,
      {
        method: 'POST',
      }
    );
  }

  /**
   * Create a charge transaction (debit from account)
   */
  async createCharge(request: CreateChargeRequest): Promise<void> {
    await this.makeRequest<object>('/payments/charge', {
      method: 'POST',
      data: request,
    });
  }

  // === ACCOUNTS API ===

  /**
   * Get user account information including balances
   */
  async getUserAccounts(
    params?: GetUserAccountsParams
  ): Promise<GetUserAccountsResponse> {
    return this.makeRequest<GetUserAccountsResponse>('/accounts/by-user', {
      params,
    });
  }

  // === UTILITY METHODS ===

  /**
   * Parse external ID for event charges (format: instanceId:userId)
   */
  parseExternalId(externalId: string): ParsedExternalId | null {
    if (!externalId) return null;
    const parts = externalId.split(':');
    if (parts.length !== 2) return null;
    return {
      instanceId: parts[0],
      userId: parts[1],
    };
  }
}

// Export singleton instance
export const billingApi = new BillingApiClient();
export default billingApi;
